import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { createUsePuck } from '@puckeditor/core'
import { ChevronDown, Monitor, Smartphone, Tablet } from 'lucide-react'
import {
  BREAKPOINTS,
  Breakpoint,
  BreakpointId,
  getBreakpoint,
} from './breakpoints'

/** Canvas width used for the Default view (no real breakpoint constraint). */
const DEFAULT_CANVAS_WIDTH = 1440

/**
 * Selector-based Puck hook. Subscribing to the whole store (`usePuck()`) would
 * re-render this header control on every editor state change (each keystroke,
 * drag, hover) and log Puck's "usePuck without a selector" warning. We only need
 * the stable `dispatch`, so we select just that — no unnecessary re-renders.
 */
const usePuckSelector = createUsePuck()

const VIEWPORT_ICONS: Partial<Record<BreakpointId, ReactNode>> = {
  desktop: <Monitor size={16} />,
  tablet: <Tablet size={16} />,
  mobile: <Smartphone size={16} />,
}

export interface BreakpointSwitcherProps {
  value: BreakpointId
  onChange: (bp: BreakpointId) => void
}

/** Human-readable width range for a breakpoint option. */
function rangeLabel(bp: Breakpoint): string {
  if (bp.id === 'default') return 'Base — applies to every size'
  if (bp.minWidth && bp.maxWidth) return `${bp.minWidth}–${bp.maxWidth}px`
  if (bp.minWidth) return `≥ ${bp.minWidth}px`
  if (bp.maxWidth) return `≤ ${bp.maxWidth}px`
  return ''
}

/**
 * Header control for the breakpoint being edited — the single source of truth.
 * Selecting here resizes the canvas AND switches the edit layer, then applies
 * that breakpoint's prop overrides. Puck's native viewport bar is hidden
 * (`controlsVisible: false`) because it only resizes the canvas and never
 * applies our overrides, so showing both would mislead. Rendered inside
 * `<Puck overrides.headerActions>` so it can use `usePuck()`.
 */
export const BreakpointSwitcher = ({
  value,
  onChange,
}: BreakpointSwitcherProps) => {
  const dispatch = usePuckSelector(s => s.dispatch)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)

  // Sets the canvas width AND hides Puck's native viewport bar — this control is
  // the single source of truth for the breakpoint, so the native bar would only
  // confuse (it resizes the canvas but never applies our prop overrides).
  const setCanvasWidth = (width: number) =>
    dispatch({
      type: 'setUi',
      ui: prev => ({
        viewports: {
          ...prev.viewports,
          current: { width, height: 'auto' },
          controlsVisible: false,
        },
      }),
    })

  // Size the canvas to the active breakpoint (and hide the native bar).
  //
  // Deferred by a frame on purpose. `useResponsivePuck` keys <Puck> by the active
  // breakpoint, so every switch REMOUNTS Puck, and on mount Puck re-initialises
  // `ui.viewports.current` from the `viewports` prop — its first entry, Desktop at
  // 1280px. Dispatching in the same commit loses that race and the canvas sticks at
  // 1280 for every breakpoint. Keyed on `value` as well so a switch that does not
  // remount still resizes.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setCanvasWidth(getBreakpoint(value)?.canvasWidth ?? DEFAULT_CANVAS_WIDTH)
    })
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // Close dropdown on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      // The panel is portalled to <body>, so it is NOT inside wrapRef. Both have
      // to be checked or clicking an option would close before it registers.
      const inside =
        wrapRef.current?.contains(t) || popRef.current?.contains(t)
      if (!inside) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  /**
   * Anchor the panel to the button in viewport coordinates.
   *
   * The panel cannot be `position: absolute` inside the header: Puck's own
   * `PuckLayout-header` grid area is `overflow: auto` at 67px tall, and
   * `PuckLayout-inner` above it is `overflow: hidden`, so a 275px panel is
   * clipped and makes the header grow its own scrollbar. Portalling to <body>
   * and positioning fixed escapes both, and survives Puck upgrades — the
   * alternative would be overriding hashed CSS-module class names.
   */
  const place = useCallback(() => {
    const btn = wrapRef.current?.querySelector('button')
    if (!btn) return
    const r = btn.getBoundingClientRect()
    const GAP = 6
    const PANEL_W = 280
    const estimatedH = popRef.current?.offsetHeight ?? 275
    // Flip above the button when there is not enough room below.
    const below = r.bottom + GAP
    const top =
      below + estimatedH > window.innerHeight - 8
        ? Math.max(8, r.top - GAP - estimatedH)
        : below
    // Keep the panel inside the viewport horizontally.
    const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_W - 8))
    setAnchor({ top, left })
  }, [])

  useLayoutEffect(() => {
    if (!open) return undefined
    place()
    window.addEventListener('resize', place)
    // `true` = capture, so scrolling any of Puck's inner scroll containers
    // repositions the panel, not just the document.
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, place])

  const select = (bp: BreakpointId) => {
    setOpen(false)
    if (bp === value) return
    setCanvasWidth(getBreakpoint(bp)?.canvasWidth ?? DEFAULT_CANVAS_WIDTH)
    onChange(bp)
  }

  const active = getBreakpoint(value)

  return (
    <div ref={wrapRef} style={{ position: 'relative', marginRight: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title="Choose the breakpoint to edit"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          fontSize: 12,
          fontWeight: 600,
          border: '1px solid var(--puck-color-grey-09, #ddd)',
          borderRadius: 6,
          background: '#fff',
          cursor: 'pointer',
        }}
      >
        <span style={{ color: 'var(--puck-color-grey-05, #888)', fontWeight: 500 }}>
          Editing:
        </span>
        {active?.label ?? value}
        <ChevronDown size={14} />
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          role="dialog"
          ref={popRef}
          style={{
            position: 'fixed',
            top: anchor?.top ?? -9999,
            left: anchor?.left ?? -9999,
            // Above Puck's own overlays; the header sits at z-index 2.
            zIndex: 9999,
            // Hidden for the first paint, before `place()` has measured. Without
            // this the panel flashes at the top-left corner of the viewport.
            visibility: anchor ? 'visible' : 'hidden',
            width: 280,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
            padding: 10,
          }}
        >
          <p
            style={{
              margin: '2px 4px 10px',
              fontSize: 12,
              lineHeight: 1.45,
              color: '#555',
            }}
          >
            <strong>Default</strong> is the base. Other breakpoints override only
            what you change. Add or remove components on Default.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {BREAKPOINTS.map(bp => {
              const isActive = bp.id === value
              return (
                <button
                  key={bp.id}
                  type="button"
                  onClick={() => select(bp.id)}
                  aria-pressed={isActive}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    border: '1px solid transparent',
                    borderRadius: 6,
                    cursor: 'pointer',
                    textAlign: 'left',
                    background: isActive
                      ? 'var(--puck-color-azure-11, #f3eefb)'
                      : 'transparent',
                  }}
                >
                  <span style={{ color: '#888', display: 'inline-flex' }}>
                    {VIEWPORT_ICONS[bp.id] ?? <Monitor size={16} />}
                  </span>
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <span
                      style={{ fontSize: 13, fontWeight: isActive ? 700 : 500 }}
                    >
                      {bp.label}
                    </span>
                    <span style={{ fontSize: 11, color: '#888' }}>
                      {rangeLabel(bp)}
                    </span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

/**
 * Initial Puck `ui` for a breakpoint — pass to `<Puck ui>`.
 *
 * Supplying `viewports.current` is what stops Puck auto-picking a viewport: its
 * canvas effect bails out early when the prop is present (`if
 * (uiProp?.viewports?.current) return`). Without it Puck selects the entry
 * closest to `window.innerWidth` and writes it with `appStoreApi.setState`, so on
 * a typical 1400px+ screen every breakpoint resolved to Desktop's 1280px canvas.
 */
export const viewportUiForBreakpoint = (bp: BreakpointId) => ({
  viewports: {
    current: {
      width: getBreakpoint(bp)?.canvasWidth ?? DEFAULT_CANVAS_WIDTH,
      height: 'auto' as const,
    },
    // This control is the single source of truth, so Puck's native bar stays hidden.
    controlsVisible: false,
    // Matches Puck's own default UI state; the list actually rendered comes from
    // the `viewports` prop, not from here.
    options: [],
  },
})

/** Puck `viewports` (shown natively) derived from the breakpoint canvas widths. */
export const RESPONSIVE_VIEWPORTS = BREAKPOINTS.filter(b => b.canvasWidth).map(
  b => ({
    width: b.canvasWidth as number,
    label: b.label,
    icon: VIEWPORT_ICONS[b.id],
  }),
)
